import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
} from "@mui/material";

/**
 * Reusable dashboard-style panel: consistent chrome (title, optional actions)
 * and a single content slot for charts, tables, or copy.
 */
const Widget = ({
  title,
  subheader,
  action,
  icon,
  children,
  variant = "outlined",
  disableHeaderDivider = false,
  contentSx = {},
  headerSx = {},
  sx = {},
  ...cardProps
}) => {
  const showHeader =
    title != null ||
    subheader != null ||
    action != null ||
    icon != null;

  const titleNode =
    icon != null ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            color: "primary.main",
            flexShrink: 0,
          }}
          aria-hidden={title == null}
        >
          {icon}
        </Box>
        {title != null ? <Box sx={{ minWidth: 0 }}>{title}</Box> : null}
      </Box>
    ) : (
      title
    );

  return (
    <Card
      variant={variant}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: { xs: 1.5, md: 2 },
        ...sx,
      }}
      {...cardProps}
    >
      {showHeader && (
        <>
          <CardHeader
            title={titleNode}
            subheader={subheader}
            action={action}
            sx={{
              py: { xs: 1.25, md: 1.5 },
              px: { xs: 1.5, md: 2 },
              "& .MuiCardHeader-title": {
                fontSize: { xs: "1rem", md: "1.05rem" },
                fontWeight: 600,
              },
              ...headerSx,
            }}
          />
          {!disableHeaderDivider && <Divider />}
        </>
      )}
      <CardContent
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          pt: showHeader ? { xs: 1.5, md: 2 } : { xs: 2, md: 2.5 },
          px: { xs: 1.5, md: 2 },
          pb: { xs: 1.5, md: 2 },
          "&:last-child": { pb: { xs: 1.5, md: 2 } },
          ...contentSx,
        }}
      >
        {children}
      </CardContent>
    </Card>
  );
};

Widget.propTypes = {
  title: PropTypes.node,
  subheader: PropTypes.node,
  action: PropTypes.node,
  icon: PropTypes.node,
  children: PropTypes.node,
  variant: PropTypes.oneOf(["elevation", "outlined"]),
  disableHeaderDivider: PropTypes.bool,
  contentSx: PropTypes.object,
  headerSx: PropTypes.object,
  sx: PropTypes.object,
};

export default Widget;
